<template>
  <div class="mobile-kds">
    <div class="top-bar-container">
      <header class="mobile-header">
        <div class="brand">
          <span class="icon">🔥</span>
          <h1>BẾP</h1>
        </div>
        <div class="time-badge">{{ currentTimeString }}</div>
      </header>

      <div class="mobile-tabs">
        <button
            class="tab-btn"
            :class="{ active: currentTab === 'pending' }"
            @click="currentTab = 'pending'"
        >
          Đang chờ ({{ pending.length }})
        </button>
        <button
            class="tab-btn"
            :class="{ active: currentTab === 'done' }"
            @click="currentTab = 'done'"
        >
          Đã xong ({{ len_mon.length }})
        </button>
      </div>
    </div>

    <main class="mobile-content">

      <div v-if="currentTab === 'pending'" class="tab-pane">
        <div v-if="pending.length === 0" class="empty-state">
          <div class="empty-emoji">🍺</div>
          <h3>Đang rảnh tay</h3>
          <p>Chưa có đơn mới</p>
        </div>

        <TransitionGroup name="card" tag="div" class="card-list" v-else>
          <div
              v-for="o in pending"
              :key="o.id"
              class="order-card"
              :class="getUrgencyClass(o.createdAt)"
          >
            <div class="card-header">
              <h2 class="table-title">Bàn {{ o.table.name }}</h2>
              <span class="timer-badge">
                ⏱ {{ getElapsedMinutes(o.createdAt) }}p
              </span>
            </div>

            <div class="card-body">
              <TransitionGroup name="fade" tag="div">
                <div
                    v-for="it in o.items"
                    :key="it.id"
                    class="food-item"
                    :class="{ 'is-done': it.status_item }"
                    @click="!it.status_item && serveItem(o.id, it)"
                >
                  <div class="info">
                    <div class="name">{{ it.food.name }}</div>
                    <div v-if="it.note" class="note-box">
                      <span class="note-icon">⚠️</span> {{ it.note }}
                    </div>
                  </div>

                  <div class="qty-wrapper">
                    <div class="qty">{{ it.qty }} suất</div>
                  </div>
                </div>
              </TransitionGroup>
            </div>

            <div class="card-footer">
              <button class="btn-bump" @click="serve(o)">
                ✅ Xong bàn này
              </button>
            </div>
          </div>
        </TransitionGroup>
      </div>

      <div v-if="currentTab === 'done'" class="tab-pane">
        <div v-if="len_mon.length === 0" class="empty-state">
          <p>Chưa có đơn hoàn thành</p>
        </div>

        <div class="card-list" v-else>
          <div v-for="o in len_mon" :key="o.id" class="order-card is-completed">
            <div class="card-header">
              <h2 class="table-title">Bàn {{ o.table.name }}</h2>
              <span class="done-badge">Đã lên</span>
            </div>

            <div class="card-body">
              <div v-for="it in o.items" :key="it.id || it.food.name" class="food-item is-done">
                <div class="info">
                  <div class="name">{{ it.food.name }}</div>
                </div>
                <div class="qty-wrapper">
                  <div class="qty">{{ it.qty }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { getOrderByTable, lenMon, serveOrderItems } from '../services/orders'
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import ting from '../assets/sounds/nhac_chuong_thong_bao_iphone-www_tiengdong_com.mp3'

let stompClient = null;
const orders = ref([])
const currentTab = ref('pending')
const now = ref(Date.now())
let timerInterval = null
let soundEnabled = true
let soundTimeout = null

// ==================== AUDIO ====================
function playNotification() {
  if (!soundEnabled) return
  const audio = new Audio(ting)
  audio.play().catch(() => {})
}

function disableSoundTemporarily() {
  soundEnabled = false
  if (soundTimeout) clearTimeout(soundTimeout)
  soundTimeout = setTimeout(() => { soundEnabled = true }, 1500)
}

// ==================== API & WEBSOCKET ====================
async function loadOrders() {
  orders.value = await getOrderByTable()
}

onMounted(async () => {
  await loadOrders();

  timerInterval = setInterval(() => { now.value = Date.now() }, 10000)

  stompClient = new Client({
    webSocketFactory: () => new SockJS('/ws'),
    reconnectDelay: 5000,
    debug: (str) => console.log(str),
    onConnect: () => {
      stompClient.subscribe('/topic/orders', () => {
        playNotification()
        loadOrders()
      })
    }
  });
  stompClient.activate();
});

onUnmounted(() => {
  if (stompClient) stompClient.deactivate();
  if (soundTimeout) clearTimeout(soundTimeout);
  if (timerInterval) clearInterval(timerInterval);
});

// ==================== LOGIC ====================
const currentTimeString = computed(() => {
  return new Date(now.value).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
})

function getElapsedMinutes(createdAt) {
  if (!createdAt) return 0;
  return Math.max(0, Math.floor((now.value - new Date(createdAt).getTime()) / 60000));
}

function getUrgencyClass(createdAt) {
  const mins = getElapsedMinutes(createdAt);
  if (mins >= 20) return 'urgent';
  if (mins >= 10) return 'warning';
  return 'normal';
}

const pending = computed(() => {
  return orders.value
      .map(o => {
        const pendingItems = o.items.filter(it => !it.status_item)
        if (pendingItems.length === 0) return null
        return { ...o, items: o.items }
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
})

const len_mon = computed(() => orders.value.filter(o => o.table.status === '2'))

// ==================== ACTIONS ====================
async function serve(o) {
  disableSoundTemporarily()
  await lenMon(o.id)
  await loadOrders()
}

async function serveItem(orderId, item) {
  disableSoundTemporarily()

  // Optimistic UI Update (Phản hồi chạm mượt mà ngay lập tức)
  const order = orders.value.find(o => o.id === orderId)
  if (order) {
    const targetItem = order.items.find(i => i.id === item.id)
    if (targetItem) targetItem.status_item = true

    const hasPending = order.items.some(i => !i.status_item)
    if (!hasPending) {
      setTimeout(async () => {
        await lenMon(orderId)
        await loadOrders()
      }, 400)
      return;
    }
  }

  try {
    await serveOrderItems(orderId, item.id)
    await loadOrders()
  } catch (e) {
    console.error(e)
    await loadOrders()
  }
}
</script>

<style scoped>
/* ================= THIẾT LẬP GỐC CHO MOBILE (TONE XÁM XANH DỊU MẮT) ================= */
.mobile-kds {
  display: flex;
  flex-direction: column;
  height: 100vh;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #0f172a;
  color: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* ================= FIXED TOP BAR ================= */
.top-bar-container {
  background-color: #1e293b;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  border-bottom: 1px solid #334155;
  z-index: 10;
}

.mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #f8fafc;
}

.time-badge {
  background: #334155;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #38bdf8;
}

/* Mobile Tabs */
.mobile-tabs {
  display: flex;
  background: #0f172a;
  border-radius: 10px;
  padding: 4px;
}

.tab-btn {
  flex: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s;
}

.tab-btn.active {
  background: #38bdf8;
  color: #0f172a;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* ================= MAIN CONTENT ================= */
.mobile-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  -webkit-overflow-scrolling: touch;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ================= ORDER CARD (MOBILE) ================= */
.order-card {
  background: #1e293b;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-left: 6px solid #38bdf8;
}

/* Cảnh báo thời gian - Màu ấm pastel */
.order-card.warning { border-left-color: #fbbf24; }
.order-card.warning .timer-badge { color: #fbbf24; background: rgba(251, 191, 36, 0.15); }

/* Khẩn cấp - Màu đỏ cam pastel */
.order-card.urgent { border-left-color: #f87171; }
.order-card.urgent .timer-badge { color: #fff; background: #f87171; animation: pulse 2s infinite; }

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: rgba(255,255,255,0.02);
  border-bottom: 1px solid #334155;
}

.table-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #f8fafc;
}

.timer-badge {
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 8px;
  color: #94a3b8;
}

.done-badge {
  font-size: 13px;
  color: #34d399;
  font-weight: 700;
}

/* ================= DANH SÁCH MÓN ĂN (TỐI ƯU KHÔNG GIAN) ================= */
.card-body {
  padding: 4px 16px;
}

.food-item {
  display: flex;
  justify-content: space-between; /* Đẩy 2 khối dạt ra 2 mép trái/phải */
  align-items: center; /* Căn giữa theo chiều dọc */
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px dashed #334155; /* Nét đứt */
  min-height: 60px; /* Vùng bấm to hơn */
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.food-item:last-child {
  border-bottom: none;
}

/* Khối thông tin bên trái */
.info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.name {
  font-size: 17px;
  font-weight: 700;
  color: #f8fafc;
  line-height: 1.3;
  transition: all 0.3s;
}

/* Khối ghi chú nổi bật */
.note-box {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  background: rgba(251, 191, 36, 0.1);
  border-left: 3px solid #fbbf24;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  color: #fbbf24;
  font-weight: 500;
  width: fit-content;
  transition: all 0.3s;
}

.note-icon {
  font-size: 12px;
  margin-top: 2px;
}

/* Khối số lượng bên phải */
.qty-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty {
  font-size: 18px; /* Số to rõ ràng */
  font-weight: 800;
  color: #0f172a; /* Chữ tối màu tương phản */
  background: #38bdf8; /* Nền xanh sáng */
  padding: 8px 18px;
  border-radius: 12px;
  min-width: 55px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(56, 189, 248, 0.2);
  transition: all 0.3s;
}

/* Trạng thái món đã tick (xong) */
.food-item.is-done {
  opacity: 0.5;
  pointer-events: none;
}

.food-item.is-done .name {
  text-decoration: line-through;
  color: #64748b;
}

.food-item.is-done .qty {
  background: #334155;
  color: #94a3b8;
  box-shadow: none;
}

.food-item.is-done .note-box {
  filter: grayscale(100%);
  opacity: 0.7;
}

/* ================= FOOTER BÀN & NÚT XONG ================= */
.card-footer {
  padding: 12px 16px;
  background: transparent;
  border-top: 1px solid #334155;
}

.btn-bump {
  width: 100%;
  padding: 14px;
  background: #10b981;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 800;
  transition: transform 0.1s;
}

.btn-bump:active {
  transform: scale(0.98);
  background: #059669;
}

/* ================= TRẠNG THÁI RỖNG ================= */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  color: #64748b;
}

.empty-emoji {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #94a3b8;
  margin: 0 0 8px 0;
}

/* ================= ANIMATION ================= */
.card-enter-active, .card-leave-active { transition: all 0.3s ease; }
.card-enter-from, .card-leave-to { opacity: 0; transform: translateY(20px) scale(0.95); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
</style>